import { Injectable } from '@angular/core';
import { WorkshopJob } from '../../models/workshop-job';
import { RetailItemSale } from '../../models/retail-item-sale';
import { ComponentStore } from '@ngrx/component-store';
import { Customer } from '../../models';
import { WorkshopJobService } from '../../shared/services/workshop-job.service';
import { pipe, withLatestFrom } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CustomerService } from '../../shared';
import { CustomerEquipment } from '../../models/customer-equipment';
import {
  WorkshopJobEventBase,
  WorkshopJobEventType
} from '../../models/workshop-job-event';
import { WorkshopJobEventsService } from '../../shared/services/workshop-job-events.service';
import { RepairDocketService } from '../../shared/services/repair-docket.service';
import { RepairDocket } from '../../models/repair-docket';
import { ItemAttributesService } from '../../shared/services/item-attributes.service';
import { ItemAttribute } from '../../models/item';
import { WorkshopJobTemplateService } from 'src/app/shared/services/workshop-job-template.service';
import { WorkshopJobTemplate } from 'src/app/models/workshop-job-template';
import { tapResponse } from '@ngrx/operators';
import { ToastrService } from 'ngx-toastr';

interface ViewWorkshopJobStoreState {
  job: WorkshopJob<any>;
  customer: Customer;
  loading: boolean;
  loadingCustomer: boolean;
  loadingEvents: boolean;
  parts: RetailItemSale[];
  events: WorkshopJobEventBase<any>[];
  attributes: ItemAttribute[];
  repairDocket: RepairDocket;
  template: WorkshopJobTemplate | null;
}

const initialState: ViewWorkshopJobStoreState = {
  job: null,
  customer: null,
  loading: true,
  loadingCustomer: true,
  loadingEvents: true,
  repairDocket: null,
  parts: [],
  attributes: [],
  events: [],
  template: null
};

@Injectable()
export class ViewWorkshopJobStoreService extends ComponentStore<ViewWorkshopJobStoreState> {
  constructor(
    private workshopJobService: WorkshopJobService,
    private customerService: CustomerService,
    private workshopJobEventsService: WorkshopJobEventsService,
    private itemAttributesService: ItemAttributesService,
    private repairDocketService: RepairDocketService,
    private workshopJobTemplateService: WorkshopJobTemplateService,
    private toastr: ToastrService
  ) {
    super(initialState);
  }

  readonly jobAndEventsLoading$ = this.select(
    (s) => s.loading && s.loadingEvents
  );
  readonly job$ = this.select((s) => s.job);
  readonly customer$ = this.select((s) => s.customer);
  readonly loading$ = this.select((s) => s.loading);
  readonly customerLoading$ = this.select((s) => s.loadingCustomer);
  readonly repairDocket$ = this.select((s) => s.repairDocket);
  readonly eventsOrdered$ = this.select((s) => s.events).pipe(
    map((events) =>
      events.sort(
        (a, b) =>
          new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf()
      )
    )
  );
  readonly template$ = this.select((s) => s.template);
  readonly attributes$ = this.select((s) => s.attributes);

  readonly loadJob = this.effect<number>(
    pipe(
      tap(() =>
        this.patchState({
          loading: true,
          loadingCustomer: true,
          loadingEvents: true,
          repairDocket: null,
          parts: [],
          events: [],
          customer: null,
          template: null
        })
      ),
      switchMap((id) => this.workshopJobService.getJob(id)),
      tapResponse(
        (job: WorkshopJob<any>) => {
          this.patchState({
            job,
            loading: false
          });
          this.loadEvents(job);
          if (job.repairable_type === 'customerEquipment') {
            this.loadCustomer(
              (job.repairable as CustomerEquipment).customer_id
            );
            this.loadDocket(job.repair_docket_id);
          } else {
            this.patchState({ loadingCustomer: false });
          }
        },
        (error) => {
          console.log(error);
        }
      ),
      tap(({ template_id }) => template_id && this.loadTemplate(template_id))
    )
  );

  readonly clearAttributes = this.updater((state) => ({
    ...state,
    attributes: []
  }));

  readonly updateJob = this.effect<Partial<WorkshopJob<any>>>(
    pipe(
      withLatestFrom(
        this.job$.pipe(
          filter(Boolean),
          take(1),
          map(({ id }) => id)
        )
      ),
      switchMap(([update, id]) =>
        this.workshopJobService.update(id, update).pipe(
          tapResponse(
            (job: WorkshopJob<any>) => {
              this.patchState({ job });
            },
            (error) => {
              console.log(error);
            }
          )
        )
      )
    )
  );

  private readonly loadDocket = this.effect<number>(
    pipe(
      switchMap((id) => this.repairDocketService.getRepairDocket(id)),
      tapResponse(
        (repairDocket: RepairDocket) => {
          this.patchState({
            repairDocket
          });
        },
        (error) => {
          console.log(error);
        }
      )
    )
  );

  private readonly loadCustomer = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loadingCustomer: true })),
      switchMap((id) => this.customerService.getCustomer(id)),
      tapResponse(
        (customer: Customer) => {
          this.patchState({
            customer,
            loadingCustomer: false
          });
        },
        (error) => {
          console.log(error);
        }
      )
    )
  );

  private readonly loadTemplate = this.effect<number>(
    pipe(
      switchMap((id) => this.workshopJobTemplateService.get(id)),
      tapResponse(
        (template: WorkshopJobTemplate) => {
          this.patchState({ template });
        },
        (error) => {
          console.log(error);
        }
      )
    )
  );

  private readonly loadEvents = this.effect<WorkshopJob<any>>(
    pipe(
      tap(() => this.patchState({ loadingEvents: true })),
      switchMap((job) =>
        this.workshopJobEventsService.getForJob(job.id).pipe(
          tapResponse(
            (events: WorkshopJobEventBase<any>[]) => {
              const bookedInEvent = this.getBookedInEventFromJob(job);
              this.patchState({
                events: [...events, bookedInEvent],
                loadingEvents: false
              });
            },
            (error) => {
              console.log(error);
            }
          )
        )
      )
    )
  );

  readonly addEvent = this.effect<{
    jobEvent: Partial<WorkshopJobEventBase<any>>;
    onSuccess: () => void;
  }>(
    pipe(
      withLatestFrom(
        this.job$.pipe(
          filter(Boolean),
          map(({ id, repairable_type, repairable_id }) => ({
            jobId: id,
            repairable_type,
            repairable_id
          }))
        )
      ),
      switchMap(
        ([
          { jobEvent, onSuccess },
          { jobId, repairable_type, repairable_id }
        ]) =>
          this.workshopJobEventsService.addEvent(jobId, jobEvent).pipe(
            tapResponse(
              (newEvent: WorkshopJobEventBase<any>) =>
                this.appendEvent(newEvent),
              (error) => {
                this.toastr.error(
                  (error as Error).message ?? 'An error occurred adding event'
                );
              }
            ),
            tap(() => {
              // if this is a completed event for an item, we need to load the attributes in case any should be updated
              if (jobEvent.type === 'completed' && repairable_type === 'item') {
                this.getAttributesForItem(repairable_id);
              }
            }),
            tap(() => this.refreshJob()),
            tap(() => onSuccess())
          )
      )
    )
  );

  readonly updateEvent = this.effect<{
    jobEvent: Partial<WorkshopJobEventBase<any>>;
    onSuccess: () => void;
  }>(
    pipe(
      withLatestFrom(
        this.job$.pipe(
          filter(Boolean),
          map(({ id }) => id)
        )
      ),
      switchMap(([{ jobEvent, onSuccess }, jobId]) =>
        this.workshopJobEventsService
          .updateEvent(jobId, jobEvent.id, jobEvent)
          .pipe(
            tapResponse(
              (updatedEvent: WorkshopJobEventBase<any>) => {
                this.patchState((state) => ({
                  ...state,
                  events: state.events.map((e) =>
                    e.id === updatedEvent.id ? updatedEvent : e
                  )
                }));
              },
              (error) => {
                this.toastr.error(
                  (error as Error).message ?? 'An error occurred updating event'
                );
              }
            ),
            tap(() => onSuccess())
          )
      )
    )
  );

  private readonly appendEvent = this.updater<WorkshopJobEventBase<any>>(
    (state, event) => ({
      ...state,
      events: [...state.events, event]
    })
  );

  private readonly refreshJob = this.effect<void>(
    pipe(
      withLatestFrom(
        this.job$.pipe(
          filter(Boolean),
          map(({ id }) => id)
        )
      ),
      switchMap(([, jobId]) => this.workshopJobService.getJob(jobId)),
      tapResponse(
        (job: WorkshopJob<any>) => this.patchState({ job }),
        (error) => {
          console.log(error);
        }
      )
    )
  );

  private readonly getAttributesForItem = this.effect<number>(
    pipe(
      switchMap((itemId) => this.itemAttributesService.getAll(itemId)),
      tapResponse(
        (attributes: ItemAttribute[]) => {
          this.patchState({ attributes });
        },
        (error) => {
          console.log(error);
        }
      )
    )
  );

  private getBookedInEventFromJob(job): WorkshopJobEventBase<null> {
    return {
      id: 0,
      type: 'booked_in' as WorkshopJobEventType,
      created_at: job.booked_in_at,
      updated_at: job.booked_in_at,
      created_by: job.booked_in_by,
      workshop_job_id: job.id,
      details: null,
      deleted_at: null
    };
  }
}
